import { JSX, useMemo } from 'react';
import styled from 'styled-components';
import Button from 'components/ui-components/Button';
import { grey30, grey10, grey50, white, black } from 'constants/variables';
/*
 * This hook is a modified version of the following:
 * https://www.freecodecamp.org/news/build-a-custom-pagination-component-in-react/
 * https://github.com/mayankshubham/react-pagination
 */

export interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  siblingCount?: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const StyledUl = styled.ul`
  display: inline-block;
  margin: 0;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const StyledLI = styled.li`
  display: inline;
  list-style: none;
  line-height: 20px;

  &:first-child button {
    border-left-width: 1px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  &:last-child button {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const PageLink = styled(Button)`
  float: left;
  min-height: 20px;
  padding: 4px 12px;
  line-height: 20px;
  text-decoration: none;
  border: 1px solid ${grey10};
  border-left-width: 0;
  text-shadow: 1px 1px 1px ${white};
  color: ${({ disabled }) => (disabled ? grey50 : black)};
  background: ${({ className }) =>
    className === 'active'
      ? grey10
      : `linear-gradient(to bottom, ${white} 0%, ${grey10} 100%)`};
  cursor: ${({ disabled, className }) =>
    disabled || className === 'active' ? 'default' : 'pointer'};
  ${({ className }) =>
    className === 'active' && `box-shadow: inset 3px 3px 6px ${grey30};`}
`;

const Pagination = ({
  currentPage,
  totalCount,
  pageSize,
  siblingCount = 1,
  onPageChange,
  className,
}: PaginationProps) => {
  const dots = '...';

  // Range creates an array of a certain length and sets
  // the elements within it from start value to end value
  const range = (start: number, end: number): number[] => {
    const length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
  };

  const paginationRange = useMemo(() => {
    // Math.ceil is used here to create an extra page for remaining data
    const totalPageCount = Math.ceil(totalCount / pageSize);

    // The following determines how many other page number are shown
    const totalPageNumbers = siblingCount + 5;

    /* ----------------------------------------------------------------
     * CASE 1: Return full range of numbers (Number of pages is less than the pagination component shows)
     ------------------------------------------------------------------*/

    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    // Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount,
    );

    // We don't show dots when there is only one page number to be inserted between the extremes of sibling and the page limits
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    /* ----------------------------------------------------------------
     * CASE 2: No left dots to show, but right dots to be shown
     ------------------------------------------------------------------*/

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, dots, totalPageCount];
    }

    /* ----------------------------------------------------------------
     * CASE 3: No right dots to show, but left dots to be shown
     ------------------------------------------------------------------*/

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount,
      );
      return [firstPageIndex, dots, ...rightRange];
    }

    /* ----------------------------------------------------------------
     * CASE 4: Both left and right dots to be shown
     ------------------------------------------------------------------*/

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, dots, ...middleRange, dots, lastPageIndex];
    }

    return null;
  }, [totalCount, pageSize, siblingCount, currentPage]);

  // If there are less than 2 items in the pagination range we won't render the component
  if (
    currentPage === 0 ||
    !paginationRange?.length ||
    paginationRange.length < 2
  ) {
    return null;
  }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <nav role="navigation" aria-label="Pagination" className={className || ''}>
      <StyledUl>
        <StyledLI>
          <PageLink
            type="button"
            handleClick={onPrevious}
            disabled={currentPage === 1}>
            ← Previous
          </PageLink>
        </StyledLI>
        {paginationRange.map(
          (pageNumber: number | string, i: number): JSX.Element =>
            // If the pageItem is a dot, render the dots unicode character
            pageNumber === dots ? (
              <StyledLI
                key={`${dots}-${paginationRange[i - 1]}-${
                  paginationRange[i + 1]
                }`}>
                <PageLink type="button" disabled>
                  {dots}
                </PageLink>
              </StyledLI>
            ) : (
              // Render page numbers
              <StyledLI key={pageNumber}>
                <PageLink
                  type="button"
                  handleClick={() => onPageChange(Number(pageNumber))}
                  className={
                    currentPage === Number(pageNumber) ? 'active' : ''
                  }>
                  {pageNumber}
                </PageLink>
              </StyledLI>
            ),
        )}
        <StyledLI>
          <PageLink
            type="button"
            handleClick={onNext}
            disabled={currentPage === lastPage}>
            Next →
          </PageLink>
        </StyledLI>
      </StyledUl>
    </nav>
  );
};

export default Pagination;
